import React, { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom';
import { Button } from '@mui/material';

import { AppConfig } from '../../config/appConfig';

import { IContainer } from '../../types/container';

import { UtilityService } from '../../services/utilityService';

import { Icons } from '../icons';

export interface IContainerTableProps {
	data?: IContainer[];
	name: string;
	fillsPosition: string;
	status: string;
}

const ContainerTable: React.FunctionComponent<IContainerTableProps> = ({ data, name, fillsPosition, status }) => {
	const [workingData, setWorkingData] = useState<IContainer[]>([]);
	const [dataView, setDataView] = useState<IContainer[]>([]);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(AppConfig.defaultPageSize);
	const [sortField, setSortField] = useState(AppConfig.containerListDefaultSortField);
	const [sortAscending, setSortAscending] = useState(true);

	useEffect(() => {
		setWorkingData([...data ?? []]);
	}, [data]);

	const handleHeaderClick = (field: string) => {
		if (sortField == field) {
			setSortAscending(!sortAscending);
		}
		else {
			setSortAscending(true);
			setSortField(field);
		}
	}

	useEffect(() => {
		if (workingData) {
				let newData = workingData.filter((item) => {
					if (name?.trim() && !item.name.toLowerCase().includes(name.toLowerCase().trim())) {
						return false;
					}
					if (fillsPosition != "All" && parseBoolean(fillsPosition) != item.fillsPosition) {
						return false;
					}
					if (status != "All" && parseBoolean(status) != item.isActive) {
						return false;
					}

					return true;
				}).slice();

			// Start sort as descending if the field name starts with "-"
			let _sortField = sortField;
			if (sortField.startsWith("-")) {
				setSortAscending(false);
				_sortField = sortField.substring(1);
				setSortField(_sortField);
			}

			if (sortField) {
				switch (sortField) {
					case "name": {
						newData.sort((a, b) => UtilityService.compareStrings(a.name, b.name));
						break;
					}
					case "weight": {
						newData.sort((a, b) => UtilityService.compareNumbers(a.weight, b.weight));
						break;
					}
					case "fillsPosition": {
						newData.sort((a, b) => UtilityService.compareBooleans(a.fillsPosition, b.fillsPosition));
						break;
					}
					case "status": {
						newData.sort((a, b) => UtilityService.compareBooleans(a.isActive, b.isActive));
						break;
					}
					default: {
						newData.sort((a, b) => UtilityService.compareStrings(a.name, b.name));
						break;
					}
				}

				if (!sortAscending) newData.reverse();

				setDataView(newData);
				setPage(0);
			}
		}
	}, [workingData, name, fillsPosition, status, sortField, sortAscending])

	const handlePageBack = () => {
		setPage(page - 1);
	}

	const handlePageForward = () => {
		setPage(page + 1);
	}

	return (
		<div className="hwi-entity-search-results-table">
			<div className="headers">
				<div className="name-col">
					<p onClick={() => handleHeaderClick("name")}>
						Name
						{" "}
						{(sortField == "name" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "name" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="weight-col">
					<p onClick={() => handleHeaderClick("weight")}>
						Weight
						{" "}
						{(sortField == "weight" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "weight" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="dimensions-col nosort">
					<p>
						Dimensions
					</p>
				</div>
				<div className="fills-position-col">
					<p onClick={() => handleHeaderClick("fillsPosition")}>
						Fills Position
						{" "}
						{(sortField == "fillsPosition" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "fillsPosition" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="status-col">
					<p onClick={() => handleHeaderClick("status")}>
						Status
						{" "}
						{(sortField == "status" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "status" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="controls-col">{""}</div>
			</div>
			<div className="data">
				{dataView?.length > 0 ?
					(
						<>
							{dataView?.slice(page * pageSize, (page + 1) * pageSize).map((item, index) => (
								<div className={`row${!item.isActive ? " inactive" : ""}`} key={item.id}>
									<div className="name-col"><p>{item.name}</p></div>
									<div className="weight-col"><p>{item.weight} lbs</p></div>
									<div className="dimensions-col"><p>{`${item.length ?? "?"}" x ${item.width ?? "?"}" x ${item.height ?? "?"}"`}</p></div>
									<div className="fills-position-col"><p>{item.fillsPosition ? "Yes" : "-"}</p></div>
									<div className="status-col"><p>{item.isActive ? "Active" : "Inactive"}</p></div>
									<div className="controls-col"><p>
										<NavLink to={"/container/" + item.id}>
											<Icons.EditItem fill="#272f50" height="14px"></Icons.EditItem>
											{" "}
											Edit
										</NavLink>
									</p></div>
								</div>
							))}
						</>
					) : (<div className="no-results">0 results</div>)}
			</div>
			<div className="pager">
				<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={page < 1} onClick={handlePageBack}>Previous</Button></div>
				<div className="pager-text">{`${(page * pageSize) + 1} to ${((page + 1) * pageSize) <= dataView.length ? ((page + 1) * pageSize) : dataView.length} of ${dataView.length}`}</div>
				<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={((page + 1) * pageSize) >= dataView.length} onClick={handlePageForward}>Next</Button></div>
			</div>
		</div>
	)
}

export default ContainerTable;